import React from 'react';
import moment from "moment";
import { FormLabel} from "react-bootstrap";
import {Button, Alert, FloatingLabelField, useDisappearingFeedback} from "fearless-common-ui";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {toast} from "react-hot-toast";
import {useParams} from "react-router-dom";
import useGet from "../../components/hooks/useGet";
import {BackendForm} from "../../components/forms";
import {Pages} from "../../components/layout";
import {defaultDateTimeFormat} from "../../utils/date";
import {adminRoutes} from "../../utils/routes";
import useApiCall from "../../components/hooks/useApiCall";
import {backendEndpoints} from "../../libs/backendEndpoints";
import Loading from "../../components/Loading";
import {addSearchParameter, concepts, Course, Group, User} from "fearless-common";
import UrlTabs from "../../components/UrlTabs";
import UserCustomFields from "../../components/forms/UserCustomFields";
import useTitle from "../../components/hooks/useTitle";
import AdminLoggedMails from "../../components/admin/AdminLoggedMails";
import AdminUserSubscriptionInfo from "../../components/admin/AdminUserSubscriptionInfo";
import InfoTooltip from "../../components/layout/InfoTooltip";
import {BoxArrowUpRight} from "react-bootstrap-icons";
import LogInAsUserButton from "../../components/admin/LogInAsUserButton";
import AdminNotes from "../../components/admin/AdminNotes";
import UserRecentActivity from "../../components/admin/logged-ui-events/UserRecentActivity";
import AdminUserAffiliationsTab from "./user/AdminUserAffiliationsTab";
import getFullName from "../../utils/getFullName";
import HttpLogsTable from "../../components/admin/http-logs/HttpLogsTable";
import Description from "../../components/Description";
import AdminUserNotifications from "./user/AdminUserNotifications";
import AdminUserLoggedUiEvents from "./user/AdminUserLoggedUiEvents";

const labelClassName = ''

/**
 * Admin detail page for a single user. Shows personal info, subscriptions,
 * course/team affiliations, sent mails, HTTP logs, notifications, and UI events
 * across dedicated tabs. Also displays recent activity and admin notes in a sidebar.
 */
function AdminUser() {
    const { userId } = useParams() as { userId: string};
    const { feedback: passwordLinkFeedback, setFeedback: setPasswordLinkFeedback } = useDisappearingFeedback();
    const { feedback: completeAccountFeedback, setFeedback: setCompleteAccountFeedback } = useDisappearingFeedback();
    const { feedback: seedContentFeedback, setFeedback: setSeedContentFeedback } = useDisappearingFeedback();
    const { data: user, error, isLoading, reload} = useGet<User & { groups: Group[]; courses: Course[] }>({
        endpoint: `/admin/users/${userId}?_withTrashed=1&_with[]=subscriptions&_with[]=custom_fields&_with[]=groups&_with[]=courses`
    })
    const { data } = useGet<{ active: number; deleted: number }>({
      endpoint: backendEndpoints.adminUserFilesCount(userId),
    });
    const { onSubmit, isSubmitting, error: resetError } = useApiCall({
        url: backendEndpoints.forgotPassword,
    });
  const { onSubmit: sendCompleteAccountMail, isSubmitting: isSendingCompleteAccountMail, error: completeAccountMailError } = useApiCall({
    url: backendEndpoints.adminSendCompleteAccountEmail(user?.id || 0),
  });
    const { onSubmit: seedContent, isSubmitting: isSeedingContent, error: seedContentError } = useApiCall({
      url: backendEndpoints.seedContent(userId),
      method: 'post',
    });
    const { onSubmit: doDelete, isSubmitting: isDeleting, error: deleteError } = useApiCall({
        url: backendEndpoints.deleteAdminUser(userId),
        method: 'delete'
    });

    useTitle(`Admin ${concepts.User}`);

    const {
        onSubmit: submitToMakeActiveAgain,
        error: activeError,
        isSubmitting: isReActivating,
    } = useApiCall({ url: backendEndpoints.restoreUser(userId), method: 'put' });

    const makeActiveAgain = () => {
        submitToMakeActiveAgain()
            .then(() => {
                reload();
                toast.success('The user is active again!');
            });
    };

    return (
        <Pages.Centred
          breadcrumbs={[
            { label: 'Admin', href: undefined },
            { label: concepts.Users, href: adminRoutes.users },
            { label: getFullName(user), href: undefined },
          ]}
          backButton={{ href: adminRoutes.users, label: concepts.Users }}
        >
            <h1>User: {user?.first_name} {user?.last_name}</h1>
            <div className="d-flex gap-4 mb-3">
                <span>Signed up: {moment(user?.created_at).format(defaultDateTimeFormat)}</span>
                <span>Last updated: {moment(user?.updated_at).format(defaultDateTimeFormat)}</span>
                <span>My files count: {data?.active} <a href={addSearchParameter(window.location.origin+adminRoutes.files, { user_id: `${user?.id}` })}><BoxArrowUpRight /></a></span>
                <span>Binned count: {data?.deleted} <a href={addSearchParameter(window.location.origin+adminRoutes.files, { user_id: `${user?.id}` })}><BoxArrowUpRight /></a></span>
            </div>
            {error && <Alert>{error}</Alert>}
            {isLoading && <Loading />}
            {user && (
              <Row>
                <Col>
                  <UrlTabs
                    baseUrl={(k) => adminRoutes.user(userId, k)}
                    queryParameter="tab"
                    defaultActiveKey="details"
                  >
                    <UrlTabs.Tab title="Details" eventKey="details">
                      <UrlTabs.TabContent>
                        <Row>
                          <Col>
                            <BackendForm
                              initialValues={user}
                              targetEndpoint={`/admin/users/${userId}?_with[]=custom_fields`}
                              targetMethod="PUT"
                              onSuccess={(resp) => {

                              }}
                            >
                              <h5>Personal information</h5>
                              <FloatingLabelField name="first_name" label="First name" />
                              <FloatingLabelField name="last_name" label="Last name" />
                              <FloatingLabelField name="username" label="Username" />
                              <FloatingLabelField
                                name="email"
                                label="Email"
                                helperText={`Email verified at: ${user.email_verified_at ? moment(user.email_verified_at).format(defaultDateTimeFormat) : 'Not yet verified'}`}
                              />
                              <FloatingLabelField
                                name="trial_ends_at"
                                label="Sign up trial period ends at"
                                type="date"
                              />

                              <div className="d-flex align-items-stretch mt-5">
                                <h5 className="my-0">
                                  Custom fields
                                  <InfoTooltip>
                                    Custom fields are optional fields that may have been filled in by the user during sign up. E.g. a student number for a Hogeschool.
                                  </InfoTooltip>
                                </h5>
                              </div>
                              <UserCustomFields />
                            </BackendForm>
                          </Col>
                          <Col>
                            <h5>Technical account details</h5>

                            <table className="table table-sm">
                              <tbody>
                              <tr>
                                <td width="col-9">
                                  <FormLabel className={labelClassName}>Enabled two-factor auth.</FormLabel>
                                </td>
                                <td>{user?.has_2fa_enabled ? 'Yes' : 'No'}</td>
                              </tr>
                              <tr>
                                <td>
                                  <FormLabel className={labelClassName}>Is on trial</FormLabel>
                                </td>
                                <td>{user?.on_trial ? 'Yes' : 'No'}</td>
                              </tr>

                              <tr>
                                <td>
                                  <FormLabel className={labelClassName}>Last version used</FormLabel>
                                </td>
                                <td>{user?.last_version_used || '-'}</td>
                              </tr>
                              </tbody>
                            </table>

                            <h5 className="mt-5">{concepts.Subscriptions}</h5>
                            <AdminUserSubscriptionInfo user={user} />

                          </Col>
                        </Row>
                      </UrlTabs.TabContent>
                    </UrlTabs.Tab>
                    <UrlTabs.Tab title={`Courses and teams`} eventKey="courses-and-teams">
                      <UrlTabs.TabContent>
                        <AdminUserAffiliationsTab user={user} />
                      </UrlTabs.TabContent>
                    </UrlTabs.Tab>
                    <UrlTabs.Tab title="Mails" eventKey="mails">
                      <UrlTabs.TabContent>
                        {user?.id && <AdminLoggedMails userId={user.id} />}
                      </UrlTabs.TabContent>
                    </UrlTabs.Tab>
                    <UrlTabs.Tab title="Developer" eventKey="developer">
                      <UrlTabs.TabContent>
                        <HttpLogsTable userId={user.id} />
                      </UrlTabs.TabContent>
                    </UrlTabs.Tab>
                    <UrlTabs.Tab title={concepts.CustomerService} eventKey="customer-service">
                      <UrlTabs.TabContent>
                        <h6>Log in as</h6>
                        <p className="small line-height-1-2">See what {user.first_name} would see when logging into the website / {concepts.Photo51} cloud.</p>
                        <LogInAsUserButton userId={user.id} />
                        <h6 className="mt-3">Reset password link</h6>
                        <Button
                          variant="outline-primary"
                          onClick={() => onSubmit({email: user.email }).then((result) => {
                            if (result) setPasswordLinkFeedback('Link sent!');
                          })}
                          size="sm"
                          className="mb-2"
                          isDoing={isSubmitting}
                        >
                          {`Send reset password link to ${user.email}`}
                        </Button>
                        {resetError && <Alert>{resetError}</Alert>}
                        {passwordLinkFeedback && <Alert variant="success">{passwordLinkFeedback}</Alert>}

                        <h6 className="mt-3">Complete account mail</h6>
                        <p className="small line-height-1-2">Send a mail to the user with a link to complete their account.</p>
                        <Button
                          variant="outline-primary"
                          onClick={() => sendCompleteAccountMail().then((result) => {
                            if (result) setCompleteAccountFeedback('Mail sent!');
                          })}
                          size="sm"
                          className="mb-2"
                          isDoing={isSendingCompleteAccountMail}
                        >
                          {`Complete account mail to ${user.email}`}
                        </Button>
                        {completeAccountMailError && <Alert>{completeAccountMailError}</Alert>}
                        {completeAccountFeedback && <Alert variant="success">{completeAccountFeedback}</Alert>}

                        <h6 className="mt-3">Seed user content</h6>
                        <p className="small line-height-1-2">
                          Seed the user's {concepts.Photo51} My Files inventory with a set of fixes
                          reagents, so the user can tes/play around with Photo51's capabilities.
                        </p>
                        <Button
                          variant="outline-primary"
                          onClick={() => seedContent().then((result) => {
                            if (result) setSeedContentFeedback('Added reagents to inventory!')
                          })}
                          size="sm"
                          className="mb-2"
                          disabled={isSeedingContent}
                          isDoing={isSeedingContent}
                        >
                          {`Seed Photo51 My Files inventory`}
                        </Button>
                        {seedContentError && <Alert>{seedContentError}</Alert>}
                        {seedContentFeedback && <Alert variant="success">{seedContentFeedback}</Alert>}

                        {user?.deleted_at ? (
                          <>
                            <h6 className="mt-3">Restore {concepts.User}</h6>
                            <Alert variant="danger" className="p-1">
                              This account was archived at {moment(user.deleted_at).format(defaultDateTimeFormat)}.
                              <br/>
                              {activeError}
                            </Alert>
                            <Button
                              size="sm"
                              variant="success"
                              onClick={makeActiveAgain}
                              isDoing={isReActivating}
                            >
                              Restore account
                            </Button>
                          </>
                        ) : (
                          <>
                            <h6 className="mt-3">Archive user</h6>
                            <p className="small line-height-1-2">
                              Archive the user account. This does NOT delete their data from the system. And
                              it does not archive its related shared data such as groups, share requests, etc.
                            </p>
                            {deleteError && <Alert>{deleteError}</Alert>}
                            <Button
                              variant="outline-danger"
                              onClick={() => doDelete().then(() => window.location.reload())}
                              size="sm"
                              className="mb-2"
                              isDoing={isDeleting}
                            >
                              Archive this account
                            </Button>
                          </>
                        )}
                      </UrlTabs.TabContent>
                    </UrlTabs.Tab>
                    <UrlTabs.Tab eventKey="notifications" title="Notifications">
                      <UrlTabs.TabContent>
                        <AdminUserNotifications userId={user.id} />
                      </UrlTabs.TabContent>
                    </UrlTabs.Tab>
                    <UrlTabs.Tab eventKey="ui-events" title="UI Events">
                      <UrlTabs.TabContent>
                        <AdminUserLoggedUiEvents userId={user.id} />
                      </UrlTabs.TabContent>
                    </UrlTabs.Tab>
                  </UrlTabs>
                </Col>
                <Col sm={4}>
                  {user?.id && <UserRecentActivity userId={user.id} />}
                  <h3>Notes</h3>
                  <Description>Notes from {concepts.CompanyName} employees</Description>
                  {user && <AdminNotes type="App\Models\User" typeId={user.id} />}
                </Col>
              </Row>
            )}
        </Pages.Centred>
    );
}

export default AdminUser;
